// src/components/pipeline/PipelineBlendView.jsx
// The shared "blend" pipeline view (funnel strip + needs-attention band +
// stage-grouped rows) used by BOTH the Listing and Buyer pipelines — Henry's
// pick from the 2026-07-15 mockups (B + C + D). Pure presentation: the pages
// keep all data wiring (listeners, filters, search, drawers) and hand this
// component the already-visible leads.
// Spec: docs/superpowers/specs/2026-07-15-pipeline-blend-redesign-design.md
import React, { useState } from "react";
import { CalendarDays, Clock } from "lucide-react";
import {
  groupPipelineView,
  focusStageRows,
  needsAttentionLead,
} from "../../utils/pipelineView";
import { parseLocalDate } from "../../utils/statusSheetData";
import { clientNames } from "../../utils/leadClients";

const fmtDate = (v) => {
  const d = parseLocalDate(v);
  return d
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
};

const RATING_STYLES = {
  A: "bg-red-100 text-red-700",
  B: "bg-amber-100 text-amber-700",
  C: "bg-indigo-100 text-indigo-700",
};

const RatingPill = ({ rating }) => {
  const r = String(rating || "").toUpperCase();
  if (!RATING_STYLES[r]) return null;
  return (
    <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${RATING_STYLES[r]}`}>
      {r}
    </span>
  );
};

const NameAndBadges = ({ lead, wonLabel }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    <span className="font-semibold text-[13.5px] text-slate-800 truncate">
      {clientNames(lead) || lead.contactName || "(unnamed lead)"}
    </span>
    <RatingPill rating={lead.rating} />
    {lead.status === "converted" && (
      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 whitespace-nowrap">
        ✓ {wonLabel}
      </span>
    )}
    {lead.status === "lost" && (
      <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">
        LOST
      </span>
    )}
    {lead.fileRequested && (
      <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 whitespace-nowrap">
        📄 File
      </span>
    )}
  </span>
);

// Next touch, best first: appointment (green) → follow-up (plain) → nothing (red).
const NextTouch = ({ lead, apptLabel }) => {
  if (String(lead.appointmentAt || "").trim())
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 font-medium whitespace-nowrap">
        <CalendarDays size={13} /> {apptLabel} {fmtDate(lead.appointmentAt)}
      </span>
    );
  if (String(lead.nextFollowUp || "").trim())
    return (
      <span className="inline-flex items-center gap-1 text-slate-600 whitespace-nowrap">
        <Clock size={13} /> Follow-up {fmtDate(lead.nextFollowUp)}
      </span>
    );
  if (lead.status !== "active")
    return <span className="text-slate-400">—</span>;
  return <span className="text-red-600 font-medium whitespace-nowrap">No follow-up</span>;
};

export default function PipelineBlendView({
  leads,
  stages,
  parkingStages = [],
  stageDot, // (stage) => JSX dot in the stage's color
  stageOptions,
  graduationStage,
  wonLabel = "Won",
  apptLabel = "Appt",
  renderSecondary, // (lead) => string/JSX — the middle column, per pipeline
  canManage,
  onOpen,
  onStageChange,
}) {
  const [focus, setFocus] = useState("ALL");
  const opts = { stages, parkingStages };
  const vm = groupPipelineView(leads, opts);

  const StageSelect = ({ lead }) =>
    canManage?.(lead) ? (
      <select
        value={lead.stage || stages[0]}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          onStageChange?.(lead, e.target.value);
        }}
        className="text-[11px] rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-slate-600 flex-shrink-0"
        title="Move to stage"
      >
        {(stageOptions || stages).map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    ) : null;

  const Row = ({ lead }) => (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen?.(lead)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen?.(lead);
      }}
      className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-indigo-50/60 transition ${
        lead.status !== "active" ? "opacity-60" : ""
      }`}
    >
      <NameAndBadges lead={lead} wonLabel={wonLabel} />
      <span className="flex-1 min-w-0 text-[12.5px] text-slate-500 truncate">
        {renderSecondary?.(lead) || ""}
      </span>
      <span className="text-[12.5px]">
        <NextTouch lead={lead} apptLabel={apptLabel} />
      </span>
      <StageSelect lead={lead} />
    </div>
  );

  const GroupLabel = ({ children, tone = "text-slate-500" }) => (
    <p className={`text-[11px] font-semibold uppercase tracking-wide ${tone} px-1 mt-4 mb-1.5 first:mt-0`}>
      {children}
    </p>
  );

  const Card = ({ children, danger }) => (
    <div
      className={`bg-white border rounded-xl overflow-hidden ${
        danger ? "border-red-300" : "border-slate-200"
      }`}
    >
      {children}
    </div>
  );

  return (
    <div>
      {/* Funnel strip */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[["ALL", "All stages", vm.total], ...stages.map((s) => [s, s, vm.counts[s] || 0])].map(
          ([key, label, count]) => {
            const on = focus === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setFocus(key)}
                className={`flex-1 min-w-[104px] text-left rounded-lg border px-3 py-2 transition ${
                  on
                    ? "bg-indigo-50 border-indigo-500"
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <span
                  className={`block text-[20px] font-semibold leading-6 tabular-nums ${
                    on ? "text-indigo-700" : "text-slate-800"
                  }`}
                >
                  {count}
                </span>
                <span
                  className={`flex items-center gap-1.5 text-[11.5px] ${
                    on ? "text-indigo-700" : "text-slate-500"
                  }`}
                >
                  {key !== "ALL" && stageDot?.(key)}
                  {label}
                  {key === graduationStage && (
                    <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 rounded px-1 py-0.5">
                      → Board
                    </span>
                  )}
                </span>
              </button>
            );
          },
        )}
      </div>

      {focus === "ALL" ? (
        <>
          {vm.needs.length > 0 && (
            <>
              <GroupLabel tone="text-red-600">
                Needs attention · {vm.needs.length}
              </GroupLabel>
              <Card danger>
                {vm.needs.map((lead) => (
                  <div
                    key={lead.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpen?.(lead)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onOpen?.(lead);
                    }}
                    className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-red-50/50 transition"
                  >
                    <NameAndBadges lead={lead} wonLabel={wonLabel} />
                    <span className="flex-1 min-w-0 text-[12.5px] text-slate-500 truncate">
                      {lead.stage || stages[0]}
                      {renderSecondary?.(lead) ? <> · {renderSecondary(lead)}</> : null}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen?.(lead);
                      }}
                      className="text-[12px] font-semibold border border-slate-300 hover:border-indigo-500 hover:text-indigo-700 rounded-lg px-2.5 py-1.5 text-slate-600 flex-shrink-0"
                    >
                      Set follow-up
                    </button>
                  </div>
                ))}
              </Card>
            </>
          )}

          {vm.groups.map(({ stage, rows }) => (
            <React.Fragment key={stage}>
              <GroupLabel>
                <span className="inline-flex items-center gap-1.5">
                  {stageDot?.(stage)} {stage} · {vm.counts[stage] || 0}
                  {stage === graduationStage && (
                    <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 rounded px-1 py-0.5 normal-case">
                      → Deals Board
                    </span>
                  )}
                </span>
              </GroupLabel>
              <Card>
                {rows.length ? (
                  rows.map((lead) => <Row key={lead.id} lead={lead} />)
                ) : (
                  <p className="px-4 py-2.5 text-[12.5px] text-slate-400">
                    Everyone here is in Needs attention above.
                  </p>
                )}
              </Card>
            </React.Fragment>
          ))}

          {vm.waiting.length > 0 && (
            <>
              <GroupLabel>Waiting · {vm.waiting.length}</GroupLabel>
              <Card>
                {vm.waiting.map((lead) => (
                  <div
                    key={lead.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpen?.(lead)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onOpen?.(lead);
                    }}
                    className="flex items-center gap-3 px-4 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-indigo-50/60 transition"
                  >
                    <NameAndBadges lead={lead} wonLabel={wonLabel} />
                    <span className="flex-1 min-w-0 text-[12.5px] text-slate-500 truncate">
                      {lead.stage}
                      {String(lead.nextFollowUp || "").trim()
                        ? ` · check in ${fmtDate(lead.nextFollowUp)}`
                        : ""}
                    </span>
                    <StageSelect lead={lead} />
                  </div>
                ))}
              </Card>
            </>
          )}

          {vm.total === 0 && (
            <Card>
              <p className="px-4 py-8 text-center text-sm text-slate-400">
                No leads match this view.
              </p>
            </Card>
          )}
        </>
      ) : (
        (() => {
          const rows = focusStageRows(leads, focus, opts);
          return rows.length ? (
            <Card>
              {rows.map((lead) => (
                <div
                  key={lead.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onOpen?.(lead)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") onOpen?.(lead);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-indigo-50/60 transition ${
                    lead.status !== "active" ? "opacity-60" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <NameAndBadges lead={lead} wonLabel={wonLabel} />
                    <p className="text-[12.5px] text-slate-500 truncate mt-0.5">
                      {renderSecondary?.(lead) || "—"}
                    </p>
                  </div>
                  <div className="text-right text-[12.5px] flex-shrink-0">
                    <NextTouch lead={lead} apptLabel={apptLabel} />
                    {needsAttentionLead(lead, opts) && (
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        nothing scheduled
                      </p>
                    )}
                  </div>
                  <StageSelect lead={lead} />
                </div>
              ))}
            </Card>
          ) : (
            <Card>
              <p className="px-4 py-8 text-center text-sm text-slate-400">
                Nothing in {focus} right now.
              </p>
            </Card>
          );
        })()
      )}
    </div>
  );
}
